/**
 * tableStructureExecutor — `edit_table`, les quatre gestes de STRUCTURE d'un
 * tableau existant (#994).
 *
 * > `create_table` (#992) crée un tableau et le remplit. Il ne savait pas
 * > ajouter une ligne à celui que l'avocat a déjà. « Ajoute une ligne au tableau
 * > des préjudices » n'avait aucun verbe — l'agent devait recréer le tableau
 * > entier, donc réécrire des cellules que personne n'avait demandé de toucher.
 *
 * ## UN verbe pour quatre gestes, et non quatre verbes
 *
 * Le fournisseur lui-même modélise ces quatre opérations comme un seul groupe
 * d'intentions, et chaque verbe du catalogue coûte ~93 tokens de consigne à
 * CHAQUE tour de CHAQUE conversation (mesuré en #995) : quatre verbes qui
 * partagent la même cible, le même receipt et le même mode d'échec en
 * coûteraient quatre fois plus pour la même capacité. Le détail de l'arbitrage
 * vit dans le TSDoc du schéma partagé (`actionCatalog.ts`).
 *
 * ## Ce qui a été MESURÉ le 2026-08-16 sur `superdoc@2.6.0`
 *
 * (`tests/ws-mocked/mesure-994-tables.spec.ts`, relevé
 * `test-results/verbes-de-table-994.json`.)
 *
 * 1. **Le locator est `{nodeId}` À PLAT**, pas `{target:{kind:'block',…}}` —
 *    c'est la seule forme que `tables.*` accepte, et elle diffère de toutes les
 *    autres surfaces du Document API.
 * 2. **Deux formes d'insertion** : ciblée (`{nodeId, rowIndex, position}`) et
 *    d'AJOUT EN FIN (`{nodeId, count}` sans index ni position ;
 *    `{nodeId, position:'last'}` pour une colonne). Les deux mesurées.
 * 3. ⚠️ **`count` est SILENCIEUSEMENT IGNORÉ en suppression** :
 *    `deleteRow({rowIndex:1, count:2})` rend `success:true` et supprime UNE
 *    ligne (5×4 → 4×4). Le schéma partagé l'interdit déjà ; cet exécuteur ne le
 *    passe JAMAIS au provider sur un geste de suppression, même si un jour le
 *    champ arrivait quand même.
 * 4. ⚠️ **Le suivi dépend de la CELLULE, pas du tableau.** Sous fenêtre
 *    `suggesting` : `insertRow` et `deleteRow` en `tracked` sont acceptés et
 *    produisent une révision chacun ; `setCellText` en `tracked` sur une cellule
 *    **préexistante** est REFUSÉ (`CAPABILITY_UNAVAILABLE: "Table mutations do
 *    not support tracked mode on v2 yet."`), alors que la même écriture sur une
 *    cellule de la ligne **qu'on vient d'insérer** passe. Du contenu qui vit
 *    dans une insertion déjà suivie n'a pas besoin de sa propre révision.
 *
 * C'est ce quatrième fait qui commande tout le verbe : `texts` ne remplit QUE la
 * ligne insérée, et un refus de cellule n'annule jamais la ligne posée — elle
 * est acquise, la jeter serait détruire un acquis pour un défaut de contenu.
 *
 * ## ⚠️ Un point NON MESURÉ, et sa conséquence
 *
 * Les révisions produites par `insertColumn` et `deleteColumn` sous fenêtre
 * `suggesting` n'ont pas été relevées (la mesure a porté sur les lignes).
 * `edit_table` déclare `reviewedOperations: ['tables.insertRow',
 * 'tables.deleteRow']` au catalogue : si un geste de COLONNE ne produisait
 * aucune identité de révision, son receipt sortirait `partial` +
 * `TRACKING_NOT_APPLIED` — c'est-à-dire « la colonne est là, mais l'avocat ne la
 * verra pas en révision », ce qui est vrai et non trompeur. Aucune valeur n'est
 * donc inventée ici, et l'issue reste actionnable en attendant la mesure.
 *
 * ## Le juge est la GÉOMÉTRIE RELUE, jamais le receipt
 *
 * `tables.get({nodeId})` est lu AVANT et APRÈS, et la géométrie doit avoir bougé
 * du delta attendu. Un `success:true` ne prouve rien : `doc.insert` HTML rendait
 * déjà `success:true` en posant un paragraphe là où on demandait une table
 * (#980), et `deleteRow` rend `success:true` en supprimant une ligne sur deux
 * demandées.
 */

import type { CanvasActionInput } from '@hector/shared-types';
import {
  abortedBeforeMutation,
  buildVerifiedReceipt,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type VerificationCheck,
} from './actionReceipt';
import { idsDeRevision } from './canvasSnapshot';
import { detailDuRefus, estUnRefus, resoudreLaCible } from './formatActionExecutors';
import { muterSousFences, revisionCourante } from './fencesDeMutation';
import { modeDeRevision, snapshot, targetSummary, type CanvasActionContext } from './textActionExecutors';

type MethodeProvider = (input?: unknown, options?: unknown) => unknown;

/** Les surfaces de table du Document API, toutes mesurées présentes en 2.6.0. */
interface SurfacesDeTable {
  tables?: {
    get?: MethodeProvider;
    insertRow?: MethodeProvider;
    deleteRow?: MethodeProvider;
    insertColumn?: MethodeProvider;
    deleteColumn?: MethodeProvider;
    setCellText?: MethodeProvider;
  };
}

type ContexteDeTable = CanvasActionContext & { doc: CanvasActionContext['doc'] & SurfacesDeTable };

export type EditTableInput = Extract<CanvasActionInput, { action: 'edit_table' }>;

type OperationDeTable = EditTableInput['operation'];

/** Le `nodeType` sous lequel `blocks.list` publie un tableau (forme `tbl:00000007`). */
const TYPE_DE_TABLE = 'table';

/**
 * Ce que chaque opération fait, en une table plutôt qu'en quatre branches.
 *
 * `methode` est le nom de la surface `tables.*` ; `axe` dit sur quelle dimension
 * la géométrie doit bouger, et c'est lui qui fabrique la postcondition.
 */
const OPERATIONS: Record<OperationDeTable, {
  methode: 'insertRow' | 'deleteRow' | 'insertColumn' | 'deleteColumn';
  axe: 'lignes' | 'colonnes';
  insertion: boolean;
  /** Comment on nomme la chose à l'avocat, dans les messages. */
  libelle: string;
}> = {
  insert_row: { methode: 'insertRow', axe: 'lignes', insertion: true, libelle: 'ligne' },
  delete_row: { methode: 'deleteRow', axe: 'lignes', insertion: false, libelle: 'ligne' },
  insert_column: { methode: 'insertColumn', axe: 'colonnes', insertion: true, libelle: 'colonne' },
  delete_column: { methode: 'deleteColumn', axe: 'colonnes', insertion: false, libelle: 'colonne' },
};

interface GeometrieDeTable { lignes: number; colonnes: number }

/**
 * La géométrie rendue par `tables.get` — `{nodeId, address, rows, columns}`.
 *
 * `undefined` quand la lecture n'aboutit pas à deux entiers : une géométrie
 * illisible n'est PAS une géométrie nulle, et la traiter comme telle ferait
 * calculer un delta contre un 0 inventé.
 */
function lireLaGeometrie(valeur: unknown): GeometrieDeTable | undefined {
  if (typeof valeur !== 'object' || valeur === null) return undefined;
  const { rows, columns } = valeur as { rows?: unknown; columns?: unknown };
  return typeof rows === 'number' && Number.isFinite(rows) && typeof columns === 'number' && Number.isFinite(columns)
    ? { lignes: rows, colonnes: columns }
    : undefined;
}

const format = (geometrie: GeometrieDeTable): string => `${geometrie.lignes}×${geometrie.colonnes}`;

/**
 * Modifier la STRUCTURE d'un tableau existant (#994).
 *
 * ## L'ordre des gardes est l'ordre du risque
 *
 * Le sélecteur, puis le type de bloc, puis les capacités, puis la géométrie
 * LUE — et seulement ensuite la mutation. Chacune de ces quatre portes refuse
 * `aborted`, donc sur un document intact : c'est la différence entre « change de
 * cible » et « va voir ce qui a bougé », et c'est elle qui évite à l'agent de
 * réinspecter un document que personne n'a touché.
 *
 * ⚠️ Les bornes d'index sont vérifiées ICI, contre la géométrie relue, et pas
 * laissées au provider : « supprime la ligne 7 » d'un tableau qui en a 4 doit
 * dire à l'agent combien il y a de lignes, ce qu'un `INVALID_INPUT` du
 * fournisseur ne dirait pas.
 *
 * ## `position` est TOUJOURS envoyée explicitement quand un index est donné
 *
 * Le schéma la rend facultative, le provider a son propre défaut, et le rang de
 * la ligne insérée — donc l'endroit où `texts` sera écrit — en dépend. Poser le
 * défaut ici (`below` / `right`) est ce qui rend ce rang calculable au lieu
 * d'être supposé.
 */
export async function executeEditTable(
  ctx: ContexteDeTable,
  input: EditTableInput,
): Promise<CanvasActionReceipt> {
  const resolue = await resoudreLaCible(ctx, input.selector, 'edit_table');
  if (!resolue.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(await snapshot(ctx)),
      code: resolue.code,
      message: resolue.message,
    });
  }
  const bloc = resolue.target;
  const pre = await snapshot(ctx);
  const operation = OPERATIONS[input.operation];

  if (bloc.nodeType !== TYPE_DE_TABLE) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'NOT_A_TABLE',
      message: `edit_table a été REFUSÉ avant toute écriture : le sélecteur résout un bloc « ${bloc.nodeType} » `
        + `(nodeId=${bloc.nodeId}), pas un tableau. Rien n'a été modifié. `
        + 'Vise le TABLEAU lui-même : ses textes de cellule sont cherchables, donc un terme qui n\'apparaît que dans '
        + 'une cellule (un intitulé de colonne, par exemple) résout le bloc de type "table". '
        + 'Si le document ne contient pas encore ce tableau, crée-le avec create_table.',
      nextAction: 'Cible un bloc dont canvas_inspect rend nodeType "table", ou appelle create_table.',
      recovery: {
        action: 'retry',
        reason: 'Rien n\'a été écrit. Ré-inspecter rendrait le même bloc, du même type : la sortie est une autre cible '
          + 'ou un autre verbe, pas une relance à l\'identique.',
      },
    });
  }

  const methode = ctx.doc.tables?.[operation.methode];
  const besoinDeCellules = input.operation === 'insert_row' && input.texts !== undefined && input.texts.length > 0;
  if (typeof ctx.doc.tables?.get !== 'function'
    || typeof methode !== 'function'
    || (besoinDeCellules && typeof ctx.doc.tables?.setCellText !== 'function')) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: `edit_table (${input.operation}) exige editor.doc.tables.get et editor.doc.tables.${operation.methode}`
        + `${besoinDeCellules ? ' et editor.doc.tables.setCellText' : ''}, que cet éditeur n'expose pas tous. `
        + 'Rien n\'a été modifié et relancer la même action échouera à l\'identique. '
        + 'Il n\'existe pas de contournement : un tableau ne se modifie pas en écrivant du texte à côté.',
    });
  }

  // ⚠️ La géométrie est lue AVANT toute mutation, et son échec de lecture est un
  // refus : sans elle, ni les bornes d'index ni le delta attendu ne peuvent être
  // établis, donc la mutation serait posée sans qu'aucune preuve ne soit
  // possible — exactement ce que ce verbe existe pour éviter.
  const avant = lireLaGeometrie(await ctx.doc.tables.get({ nodeId: bloc.nodeId }));
  if (avant === undefined) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'TABLE_GEOMETRY_UNREADABLE',
      message: `edit_table a été REFUSÉ avant toute écriture : tables.get(nodeId=${bloc.nodeId}) n'a pas rendu de `
        + 'géométrie lisible (rows/columns). Rien n\'a été modifié. Sans elle, ni la validité de l\'index ni l\'effet '
        + 'de l\'opération ne peuvent être vérifiés — et une ligne ajoutée au mauvais endroit dans le tableau de '
        + 'l\'avocat coûte plus qu\'un refus. Relis le document par canvas_inspect pour vérifier que ce tableau existe '
        + 'toujours.',
    });
  }

  const tailleAvant = avant[operation.axe];
  if (input.index !== undefined && input.index >= tailleAvant) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'INDEX_OUT_OF_RANGE',
      message: `edit_table a été REFUSÉ avant toute écriture : index=${input.index} désigne une ${operation.libelle} `
        + `qui n'existe pas. Le tableau (nodeId=${bloc.nodeId}) fait ${format(avant)}, donc les index valides de `
        + `${operation.axe} vont de 0 à ${tailleAvant - 1}. Rien n'a été modifié. `
        + (operation.insertion
          ? 'Omets index et position pour ajouter à la fin.'
          : 'Corrige l\'index, en te rappelant qu\'il est 0-based : la première ligne est 0.'),
      nextAction: operation.insertion
        ? 'Rappelle edit_table sans index ni position pour ajouter à la fin.'
        : `Rappelle edit_table avec un index entre 0 et ${tailleAvant - 1}.`,
      recovery: {
        action: 'retry',
        reason: 'Rien n\'a été écrit. La géométrie du tableau est connue et l\'index demandé est hors de ses bornes : '
          + 'le geste suivant est le même appel avec un index valide.',
      },
    });
  }
  if (!operation.insertion && tailleAvant <= 1) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'LAST_ROW_OR_COLUMN',
      message: `edit_table a été REFUSÉ avant toute écriture : le tableau (nodeId=${bloc.nodeId}) fait ${format(avant)}, `
        + `donc supprimer cette ${operation.libelle} le viderait de sa dernière ${operation.libelle}. `
        + 'Rien n\'a été modifié. Un tableau sans ligne n\'est pas un tableau : si l\'intention est de le faire '
        + 'disparaître, supprime le bloc entier avec delete_block_range en visant ce nodeId.',
      nextAction: `Appelle delete_block_range sur le bloc ${bloc.nodeId} pour supprimer le tableau entier.`,
      recovery: {
        action: 'retry',
        reason: 'Rien n\'a été écrit. Ré-inspecter rendrait le même tableau : la sortie est delete_block_range, pas '
          + 'une relance de edit_table.',
      },
    });
  }

  const nombre = operation.insertion ? (input.count ?? 1) : 1;
  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const attenduePourStructure = await revisionCourante(ctx, pre.revision);
  const entree = entreeDeLOperation(bloc.nodeId, input, operation, nombre);

  const resultat = await muterSousFences(
    ctx,
    `edit_table:tables.${operation.methode}`,
    { changeMode: modeDeRevision(ctx), expectedRevision: attenduePourStructure },
    () => methode(entree, { changeMode: modeDeRevision(ctx), expectedRevision: attenduePourStructure }),
  );

  const apres = lireLaGeometrie(await ctx.doc.tables.get({ nodeId: bloc.nodeId }));
  const attendue: GeometrieDeTable = {
    ...avant,
    [operation.axe]: operation.insertion ? tailleAvant + nombre : tailleAvant - 1,
  };
  const geometrieAuDelta = apres !== undefined
    && apres.lignes === attendue.lignes && apres.colonnes === attendue.colonnes;
  const geometrieInchangee = apres !== undefined
    && apres.lignes === avant.lignes && apres.colonnes === avant.colonnes;

  // Le remplissage n'a lieu que sur une INSERTION, et l'insertion fait toujours
  // bouger la géométrie — les deux régimes confondus (mesuré). On peut donc
  // décider de remplir avant de connaître les révisions.
  const cellules = besoinDeCellules && geometrieAuDelta && apres !== undefined
    ? await remplirLaLigneInseree(ctx, bloc.nodeId, input, apres, pre.revision)
    : undefined;

  const post = await snapshot(ctx);
  const revisionsNouvelles = idsDeRevision(post.trackedChanges)
    .filter((identite) => !revisionsAvant.includes(identite));

  /**
   * ⚠️ **Une suppression suivie ne fait PAS rétrécir la géométrie**, et c'est le
   * fait qui commande cette postcondition (mesuré le 2026-08-16,
   * `mesure-994-tables.spec.ts` §10, avec témoin en mode direct).
   *
   * Sous le régime suivi, supprimer une ligne ne la
   * retire pas : elle est marquée supprimée et reste dans le document, barrée,
   * jusqu'à ce que l'avocat accepte la révision. `tables.get` continue donc
   * d'annoncer le même nombre de lignes. Relevé, avec témoin :
   *
   * | geste | mode | géométrie | révisions |
   * |---|---|---|---|
   * | `insertRow`    | suivi  | 3×2 → **4×2** | 1 |
   * | `deleteRow`    | suivi  | 4×2 → **4×2** | 1 |
   * | `deleteRow`    | direct | 4×2 → **3×2** | 0 |
   * | `deleteColumn` | suivi  | 3×3 → **3×3** | 1 |
   *
   * Exiger le rétrécissement rendrait donc `failed` sur une suppression
   * réussie ET révisable, dans le cas normal — un FAUX NÉGATIF, sur lequel
   * l'agent rejouerait, supprimant une ligne de plus à chaque tour.
   *
   * La postcondition nomme donc les DEUX preuves légitimes d'une suppression, et
   * ne dépend d'aucun mode DÉCLARÉ : le `changeMode` demandé ne promet rien
   * (#990, #992), seul l'état relu tranche.
   */
  const suppressionProuvee = geometrieAuDelta || (geometrieInchangee && revisionsNouvelles.length > 0);
  const conforme = operation.insertion ? geometrieAuDelta : suppressionProuvee;

  const controles: VerificationCheck[] = [{
    name: 'table_geometry_delta',
    passed: conforme,
    message: conforme
      ? (geometrieAuDelta
        ? `Relecture : le tableau passe de ${format(avant)} à ${format(attendue)}, comme demandé.`
        // `format(avant)` et non `format(apres)` : dans cette branche la
        // géométrie est INCHANGÉE, les deux sont donc égales — et `avant` est
        // non-optionnel, ce qui évite une assertion de non-nullité.
        : `Relecture : le tableau reste ${format(avant)} et porte ${revisionsNouvelles.length} révision(s) neuve(s) — `
          + `la ${operation.libelle} est marquée supprimée, pas retirée. C'est le comportement normal du suivi des `
          + 'modifications : elle disparaîtra quand l\'avocat acceptera la révision. N\'appelle pas edit_table une '
          + 'seconde fois, tu marquerais une autre ligne.')
      : apres === undefined
        ? `L'éditeur a répondu ${estUnRefus(resultat) ? 'un refus' : 'un succès'} mais tables.get ne rend plus de `
          + `géométrie lisible pour le tableau ${bloc.nodeId} : l'effet ne peut pas être prouvé. `
          + 'Relis le document par canvas_inspect avant toute autre action.'
        : `Le tableau ${bloc.nodeId} se relit ${format(apres)} alors que ${input.operation} devait le porter de `
          + `${format(avant)} à ${format(attendue)}, et aucune révision neuve ne prouve une suppression suivie`
          + (estUnRefus(resultat) ? ` — l'éditeur a refusé : ${detailDuRefus(resultat)}. ` : '. ')
          + 'Ne rejoue pas l\'appel à l\'identique tant que tu n\'as pas relu le tableau : s\'il a partiellement bougé, '
          + 'le rejeu ajouterait ou retirerait une ligne de plus.',
  }];
  if (cellules !== undefined) controles.push(controleDesCellules(cellules, bloc.nodeId));

  ctx.verifierEpoque('edit_table:receipt');

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [targetSummary(bloc, resolue.candidateCount)],
    provider: resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    verification: controles,
    effects: [{
      kind: 'edit_table',
      // `tracked` est une DEMANDE, pas une affirmation : le receipt la rabat sur
      // ce que la relecture des identités de révision établit vraiment (#990).
      reviewability: 'tracked',
      description: `${operation.insertion ? 'Ajout' : 'Suppression'} de ${nombre} ${operation.libelle}(s) dans le `
        + `tableau ${bloc.nodeId}`
        + (cellules === undefined ? '.' : `, dont ${cellules.posees} cellule(s) remplies en texte brut.`),
      nodeIds: [bloc.nodeId],
    }],
  });
}

/**
 * L'entrée exacte de l'appel provider — quatre gestes, deux grammaires.
 *
 * ⚠️ `count` n'est JAMAIS posé sur une suppression, même s'il arrivait dans
 * l'entrée : le provider le rendrait `success:true` en ne supprimant qu'une
 * ligne, et le receipt annoncerait à l'avocat une suppression qui n'a pas eu
 * lieu. Le schéma partagé ferme déjà la porte ; cette ligne-ci ferme la
 * fenêtre.
 */
function entreeDeLOperation(
  nodeId: string,
  input: EditTableInput,
  operation: (typeof OPERATIONS)[OperationDeTable],
  nombre: number,
): Record<string, unknown> {
  const cle = operation.axe === 'lignes' ? 'rowIndex' : 'columnIndex';
  if (!operation.insertion) return { nodeId, [cle]: input.index };
  if (input.index === undefined) {
    // Forme d'AJOUT EN FIN, mesurée : `{nodeId, count}` pour une ligne,
    // `{nodeId, position:'last'}` pour une colonne — les deux sans index.
    return operation.axe === 'lignes'
      ? { nodeId, count: nombre }
      : { nodeId, position: 'last', ...(nombre === 1 ? {} : { count: nombre }) };
  }
  return {
    nodeId,
    [cle]: input.index,
    position: input.position ?? (operation.axe === 'lignes' ? 'below' : 'right'),
    ...(nombre === 1 ? {} : { count: nombre }),
  };
}

interface RemplissageDeLigne {
  rang: number;
  posees: number;
  echecs: Array<{ colonne: number; detail: string }>;
  surplus: number;
}

/**
 * Remplit la ligne QU'ON VIENT D'INSÉRER, cellule par cellule.
 *
 * ⚠️ Le rang se CALCULE depuis la géométrie relue, jamais depuis l'entrée : sans
 * index c'est la dernière ligne du tableau tel qu'il est MAINTENANT, et avec
 * index c'est `index` ou `index+1` selon la position — que cet exécuteur envoie
 * toujours explicitement, précisément pour que ce calcul soit vrai.
 *
 * ⚠️ Un refus de cellule N'ANNULE PAS la ligne posée. La ligne est un acquis ;
 * la reprendre pour un défaut de contenu détruirait plus que le défaut ne coûte.
 * Le refus est consigné et rendu au modèle, comme `create_table` le fait de ses
 * cellules en échec. C'est aussi le seul endroit où le refus mesuré
 * `CAPABILITY_UNAVAILABLE: "Table mutations do not support tracked mode on v2
 * yet."` peut encore apparaître — il ne devrait pas, la cellule appartenant à une
 * insertion suivie, mais l'exécuteur ne le PARIE pas.
 */
async function remplirLaLigneInseree(
  ctx: ContexteDeTable,
  nodeId: string,
  input: EditTableInput,
  apres: GeometrieDeTable,
  repli: Parameters<typeof revisionCourante>[1],
): Promise<RemplissageDeLigne> {
  const textes = input.texts ?? [];
  const rangBrut = input.index === undefined
    ? apres.lignes - 1
    : input.position === 'above' ? input.index : input.index + 1;
  const rang = Math.max(0, Math.min(rangBrut, apres.lignes - 1));
  const aRemplir = Math.min(textes.length, apres.colonnes);
  const echecs: Array<{ colonne: number; detail: string }> = [];
  let posees = 0;

  for (let colonne = 0; colonne < aRemplir; colonne++) {
    const texte = textes[colonne] ?? '';
    if (texte.trim().length === 0) continue;
    const attendue = await revisionCourante(ctx, repli);
    const resultat = await muterSousFences(
      ctx,
      `edit_table:setCellText:${rang},${colonne}`,
      { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
      () => ctx.doc.tables?.setCellText?.({ nodeId, rowIndex: rang, columnIndex: colonne, text: texte }, {
        changeMode: modeDeRevision(ctx),
        expectedRevision: attendue,
      }),
    );
    if (estUnRefus(resultat)) echecs.push({ colonne, detail: detailDuRefus(resultat) });
    else posees += 1;
  }

  return { rang, posees, echecs, surplus: Math.max(0, textes.length - apres.colonnes) };
}

/** Ce que le remplissage a donné, dit au modèle sans jamais faire croire à un échec du geste. */
function controleDesCellules(remplissage: RemplissageDeLigne, nodeId: string): VerificationCheck {
  const rien = remplissage.echecs.length === 0 && remplissage.surplus === 0;
  return {
    name: 'table_row_cells',
    passed: rien,
    message: rien
      ? `Les ${remplissage.posees} cellule(s) non vides de la ligne ${remplissage.rang} ont été remplies en texte brut `
        + '(une cellule ne porte ni gras, ni italique, ni lien).'
      : [
          remplissage.echecs.length === 0
            ? undefined
            : `L'éditeur a refusé d'écrire dans les cellules ${remplissage.echecs.map((echec) => `n°${echec.colonne + 1} (${echec.detail})`).join(', ')} `
              + `de la ligne ${remplissage.rang} du tableau ${nodeId}.`,
          remplissage.surplus === 0
            ? undefined
            : `${remplissage.surplus} texte(s) en trop : la ligne insérée n'a que ${remplissage.posees + remplissage.echecs.length} `
              + 'colonne(s), le surplus n\'a été écrit nulle part. Ajoute d\'abord une colonne (operation '
              + '"insert_column") si le tableau doit en porter davantage.',
          '⚠️ La ligne a bien été AJOUTÉE et les autres cellules sont en place : ne rejoue pas edit_table, tu '
            + 'ajouterais une seconde ligne. Complète les cellules manquantes à la main ou signale-le à l\'avocat.',
        ].filter((phrase): phrase is string => phrase !== undefined).join(' '),
  };
}
